import axios from '@axios'
import { onUnmounted, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// Лимит имён в одном запросе — совпадает с валидацией бэкенда
const BATCH_SIZE = 25
const MAX_NAME_LENGTH = 255

/** Ключ словаря статистики: имя без регистра и крайних пробелов */
export const artistStatsKey = name => String(name || '').trim().toLowerCase()

/**
 * Популярность ненайденных в базе артистов в Spotify и Last.fm (POST /generator/artists/external-stats).
 * Работает без store — используется и на импорте, и в генераторе.
 *
 * statsByName — { [ключ]: { name, spotify, lastfm, errors } }; при сбое пачки имена получают
 * заглушку с errors ['spotify', 'lastfm'] и повторно не запрашиваются.
 * loadingNames — { [ключ]: true } для имён, которые сейчас грузятся.
 * loadFor(names) — догружает недостающее пачками по 25, последовательно; новый вызов
 * отменяет предыдущий прогон (недогруженные имена надо передать снова — вызывайте с полным списком).
 */
export default function useArtistExternalStats() {
  const toast = useToast()

  const statsByName = ref({})
  const loadingNames = ref({})

  // Номер текущего прогона: ответы устаревших прогонов и после размонтирования игнорируются
  let generation = 0

  onUnmounted(() => {
    generation += 1
  })

  const markFailed = (namesByKey, keys) => {
    const failedStats = {}
    keys.forEach(key => {
      failedStats[key] = {
        name: namesByKey[key], spotify: null, lastfm: null, errors: ['spotify', 'lastfm'],
      }
    })
    statsByName.value = { ...statsByName.value, ...failedStats }
  }

  const finishLoading = keys => {
    const nextLoadingNames = { ...loadingNames.value }
    keys.forEach(key => { delete nextLoadingNames[key] })
    loadingNames.value = nextLoadingNames
  }

  const loadFor = async names => {
    generation += 1
    const currentGeneration = generation
    // Имена прерванного прогона больше не грузятся — новый прогон подберёт их сам
    loadingNames.value = {}

    const namesByKey = {}
    const requestedNames = Array.isArray(names) ? names : []
    requestedNames.forEach(name => {
      const trimmedName = String(name || '').trim()
      const key = artistStatsKey(trimmedName)
      if (!key || trimmedName.length > MAX_NAME_LENGTH) return
      if (statsByName.value[key] || namesByKey[key]) return
      namesByKey[key] = trimmedName
    })

    const keys = Object.keys(namesByKey)
    if (!keys.length) return

    const initialLoadingNames = {}
    keys.forEach(key => { initialLoadingNames[key] = true })
    loadingNames.value = initialLoadingNames

    let hasFailedBatch = false

    for (let offset = 0; offset < keys.length; offset += BATCH_SIZE) {
      const batchKeys = keys.slice(offset, offset + BATCH_SIZE)
      try {
        // Пачки грузим строго по очереди, чтобы не долбить Spotify/Last.fm параллельно
        // eslint-disable-next-line no-await-in-loop
        const response = await axios.post('/generator/artists/external-stats', {
          names: batchKeys.map(key => namesByKey[key]),
        })
        if (currentGeneration !== generation) return

        const loadedStats = {}
        const items = (response.data && response.data.data) || []
        items.forEach(item => {
          loadedStats[artistStatsKey(item.name)] = item
        })
        statsByName.value = { ...statsByName.value, ...loadedStats }
      } catch (error) {
        if (currentGeneration !== generation) return
        hasFailedBatch = true
        markFailed(namesByKey, batchKeys)
      }
      finishLoading(batchKeys)
    }

    if (hasFailedBatch) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Не удалось загрузить популярность части артистов',
          icon: 'AlertTriangleIcon',
          variant: 'warning',
        },
      })
    }
  }

  return {
    statsByName,
    loadingNames,
    loadFor,
  }
}
